import React from 'react';
import * as Sentry from '@sentry/react';
import { SparklesIcon } from '@heroicons/react/20/solid';
import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '@oysterjs/ui/dialog';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  CaraWorkspaceArtifactType,
  SubscriptionEntitlement,
  type CaraWorkspaceFieldsFragment,
  type ProducerInformationFieldsFragment
} from '~/api/graphql';
import { useOptionalCurrentAgencyUser } from '~/components/current-user';
import {
  buildArtifactStaticsUrl,
  getArtifactTitle,
  newestArtifactVersion
} from '~/components/workspaces/lib/artifact';
import { describeFormVersion } from '~/components/workspaces/lib/template-form-editor';
import { loadPdfJs } from '~/components/workspaces/lib/template-form-pdf';
import { ArtifactVersionPicker } from './artifact-focus-html-document-version-picker';
import { ArtifactFocusShell, ArtifactIframe } from './artifact-focus-shell';
import { OpenPdfWithAttachmentsButton } from './artifact-open-pdf-with-attachments-button';
import { ProducerSignatureOptions } from './pdf-attachment-options';
import { VisibilityPopover } from '~/components/visibility/visibility-popover';
import { useVisibilityEditAllowed } from '~/components/visibility/use-visibility-edit-allowed';
import { useArtifactEditor } from '~/components/workspaces/hooks/use-artifact-editor';
import { usePdfAttachmentExport } from '~/api/data/workspaces/use-pdf-attachment-export';
import {
  useTemplateFormDownload,
  type TemplateFormDownload
} from '~/components/workspaces/hooks/use-template-form-pdf';
import { useWorkspaceAttachableArtifacts } from '~/components/workspaces/hooks/use-workspace-attachable-artifacts';

type Artifact = CaraWorkspaceFieldsFragment['artifacts'][0];

const loadTemplateFormEditor = () => import('./template-form-editor');

const TemplateFormEditor = React.lazy(() =>
  loadTemplateFormEditor().then((module) => ({ default: module.TemplateFormEditor }))
);

/** Starts fetching the editor's code and pdf.js, so opening a form waits on neither. */
const preloadTemplateFormEditor = () => {
  loadTemplateFormEditor().catch(() => undefined);
  loadPdfJs().catch(() => undefined);
};

/**
 * Whether the agency may edit form fields in the browser, which the save
 * mutation checks too. The admin app renders workspaces without a current
 * user, and gets the read-only preview.
 */
const useTemplateFormEditorEnabled = () => {
  const agencyUser = useOptionalCurrentAgencyUser();
  return !!agencyUser?.agency?.entitlements?.includes(SubscriptionEntitlement.CopilotAcordEditor);
};

/**
 * Preloads the form editor once the workspace holds a form, when the page is
 * idle, so the first form opened does not wait on pdf.js.
 */
export const usePreloadTemplateFormEditor = (workspace: CaraWorkspaceFieldsFragment) => {
  const enabled = useTemplateFormEditorEnabled();
  const holdsForm = workspace.artifacts.some(
    (artifact) => artifact.type === CaraWorkspaceArtifactType.TemplateForm
  );

  React.useEffect(() => {
    if (!enabled || !holdsForm) return;
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preloadTemplateFormEditor, { timeout: 5000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = window.setTimeout(preloadTemplateFormEditor, 2000);
    return () => window.clearTimeout(id);
  }, [enabled, holdsForm]);
};

/**
 * The version the focus shows, which leaves the dialog's version in two cases:
 * a save moves it ahead before the workspace has delivered the new version,
 * and unsaved edits hold it in place when the dialog moves on, since they can
 * only be saved against the version they were typed into.
 */
const useShownVersion = (artifact: Artifact, dirty: boolean) => {
  const [shown, setShown] = React.useState(artifact.version);
  const [followed, setFollowed] = React.useState(artifact.version);
  if (artifact.version !== followed) {
    setFollowed(artifact.version);
    if (!dirty) setShown(artifact.version);
  }
  return [shown, setShown] as const;
};

/** Who wrote a version, tinted as the editor tints the fields each of them wrote. */
const describeVersion = (version: Artifact) => {
  const author = describeFormVersion(version);
  return {
    tooltip: author.tooltip,
    badge:
      author.tone === 'user' ? (
        <Badge color="violet" className="text-xs">
          {author.label}
        </Badge>
      ) : (
        <Badge color="sky" className="text-xs">
          <SparklesIcon aria-hidden="true" className="size-3" />
          {author.label}
        </Badge>
      )
  };
};

const FormLoading = (props: { label: string }) => (
  <div className="mt-2 flex flex-grow items-center justify-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
    <Spinner size="size-4" color="neutral" />
    {props.label}
  </div>
);

/** An object URL for blob, revoked when blob changes or the caller unmounts. */
const useObjectUrl = (blob: Blob | null) => {
  const [object, setObject] = React.useState<{ blob: Blob; url: string } | null>(null);
  React.useEffect(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setObject({ blob, url });
    return () => URL.revokeObjectURL(url);
  }, [blob]);
  return object?.blob === blob ? object.url : null;
};

/**
 * The read-only form, shown from the download the editor shares rather than a
 * second render by statics. A failed download leaves the frame to ask statics
 * itself.
 */
const DownloadedPreview = (props: {
  download: TemplateFormDownload;
  src: string;
  title: string;
}) => {
  const objectUrl = useObjectUrl(props.download.pdf);
  if (props.download.error) return <ArtifactIframe src={props.src} title={props.title} />;
  if (!objectUrl) return <FormLoading label="Loading the form…" />;
  return <ArtifactIframe src={objectUrl} title={props.title} />;
};

export const TemplateFormFocus = (props: {
  workspace: CaraWorkspaceFieldsFragment;
  artifact: Artifact;
  agencyProducerInformations: ProducerInformationFieldsFragment[];
  userProducerInformations: ProducerInformationFieldsFragment[];
  onClose: () => void;
  onVersionChange?: (artifact: Artifact) => void;
  onUnsavedChangesChange?: (unsaved: boolean) => void;
}) => {
  const { artifact, workspace, onVersionChange, onUnsavedChangesChange } = props;
  const editorEnabled = useTemplateFormEditorEnabled();
  const [dirtyCount, setDirtyCount] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  /** A move that would discard the unsaved edits, held until the user confirms it. */
  const [discarding, setDiscarding] = React.useState<{ action: string; proceed: () => void }>();
  const [shownVersion, setShownVersion] = useShownVersion(artifact, dirtyCount > 0);

  // Once the workspace has the version shown, move the dialog to it, so the
  // cards and the page's link agree with what is on screen.
  React.useEffect(() => {
    if (!onVersionChange || shownVersion === artifact.version) return;
    const shown = workspace.artifacts.find(
      (candidate) => candidate.id === artifact.id && candidate.version === shownVersion
    );
    if (shown) onVersionChange(shown);
  }, [onVersionChange, shownVersion, artifact.id, artifact.version, workspace.artifacts]);

  React.useEffect(() => {
    onUnsavedChangesChange?.(dirtyCount > 0);
  }, [onUnsavedChangesChange, dirtyCount]);
  React.useEffect(() => () => onUnsavedChangesChange?.(false), [onUnsavedChangesChange]);

  const [selectedSignatureId, setSelectedSignatureId] = React.useState<string | null>(null);
  const { handleVisibilityUpdate } = useArtifactEditor({
    workspaceId: workspace.id,
    artifactId: artifact.id,
    version: artifact.version
  });
  const visibilityEditAllowed = useVisibilityEditAllowed(workspace.agencyUser?.id);

  const templateBaseUrl = buildArtifactStaticsUrl(
    'template',
    workspace.id,
    artifact.id,
    shownVersion
  );
  const title = getArtifactTitle(artifact);
  const download = useTemplateFormDownload(editorEnabled ? templateBaseUrl : null);
  React.useEffect(() => {
    if (editorEnabled) preloadTemplateFormEditor();
  }, [editorEnabled]);

  const { workspaceFiles, workspaceMessageFiles, workspaceArtifacts } =
    useWorkspaceAttachableArtifacts(workspace);

  const {
    prefixFileIds,
    suffixFileIds,
    setLocalFiles,
    attachmentCatalogList,
    onAttachmentChange,
    isOpening,
    openError,
    handleOpenPdf
  } = usePdfAttachmentExport({
    workspaceId: workspace.id,
    artifactId: artifact.id,
    version: shownVersion,
    workspaceFiles,
    workspaceMessageFiles,
    workspaceArtifacts,
    buildExtraInput: () => ({
      producerInformationId: selectedSignatureId
    })
  });

  // The page's way to move the dialog skips its own unsaved-changes guards, so
  // every move from here asks first.
  const unlessDiscarding = (action: string, proceed: () => void) => {
    if (dirtyCount > 0) setDiscarding({ action, proceed });
    else proceed();
  };

  const handleClose = () => unlessDiscarding('Closing it', props.onClose);

  const handlePick = (target: Artifact) => {
    if (target.id !== artifact.id) {
      unlessDiscarding('Opening another document', () => onVersionChange?.(target));
    } else if (target.version !== shownVersion) {
      unlessDiscarding(`Opening version ${target.version}`, () => setShownVersion(target.version));
    }
  };

  const versions = React.useMemo(
    () => workspace.artifacts.filter((candidate) => candidate.id === artifact.id),
    [workspace.artifacts, artifact.id]
  );

  const preview = editorEnabled ? (
    <DownloadedPreview download={download} src={templateBaseUrl} title={title} />
  ) : (
    <ArtifactIframe src={templateBaseUrl} title={title} />
  );
  const latestKnownVersion = newestArtifactVersion(workspace.artifacts, artifact.id)?.version;

  return (
    <ArtifactFocusShell
      title={title}
      version={shownVersion}
      versionControl={
        <ArtifactVersionPicker
          versions={versions}
          allArtifacts={workspace.artifacts}
          currentArtifact={
            versions.find((candidate) => candidate.version === shownVersion) ?? {
              ...artifact,
              version: shownVersion
            }
          }
          onVersionChange={handlePick}
          describeVersion={describeVersion}
          disabled={saving}
        />
      }
      onClose={handleClose}
      headerActions={
        <div className="flex items-center gap-2 flex-none">
          <OpenPdfWithAttachmentsButton
            onOpenPdf={handleOpenPdf}
            isOpening={isOpening}
            openError={openError}
            chevronAriaLabel="PDF export options"
            attachmentCatalog={attachmentCatalogList}
            prefixFileIds={prefixFileIds}
            suffixFileIds={suffixFileIds}
            onAttachmentChange={onAttachmentChange}
            onLocalFilesChange={setLocalFiles}
          >
            <ProducerSignatureOptions
              agencyProducerInformations={props.agencyProducerInformations}
              userProducerInformations={props.userProducerInformations}
              value={selectedSignatureId}
              onChange={setSelectedSignatureId}
            />
          </OpenPdfWithAttachmentsButton>
          {visibilityEditAllowed && (
            <VisibilityPopover visibility={artifact.visibility} onSave={handleVisibilityUpdate} />
          )}
        </div>
      }
    >
      {editorEnabled ? (
        <Sentry.ErrorBoundary fallback={preview}>
          <React.Suspense fallback={<FormLoading label="Loading the form editor…" />}>
            <TemplateFormEditor
              workspaceId={workspace.id}
              artifactId={artifact.id}
              version={shownVersion}
              latestKnownVersion={Math.max(latestKnownVersion ?? 0, shownVersion)}
              download={download}
              fallback={preview}
              loading={<FormLoading label="Loading the form editor…" />}
              onShowVersion={setShownVersion}
              onDirtyChange={setDirtyCount}
              onSavingChange={setSaving}
            />
          </React.Suspense>
        </Sentry.ErrorBoundary>
      ) : (
        preview
      )}
      {discarding && (
        <Dialog open onClose={() => setDiscarding(undefined)} size="md">
          <DialogTitle>Discard unsaved changes?</DialogTitle>
          <DialogDescription>
            You have {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'} to this form.{' '}
            {discarding.action} now discards them.
          </DialogDescription>
          <DialogActions>
            <Button type="button" color="white" onClick={() => setDiscarding(undefined)}>
              Keep editing
            </Button>
            <Button
              type="button"
              color="sky"
              onClick={() => {
                setDiscarding(undefined);
                discarding.proceed();
              }}
            >
              Discard changes
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </ArtifactFocusShell>
  );
};
